import React, { useState, useEffect } from 'react';
import { supabase } from '../global/supabase';
import HamburgerMenu from '../global/menu';
import '../global/styles.css';

const PIN_PATTERN = /^\d{6}$/;

// Admin user operations (create / bulk_create / delete / reset_password) run in the
// admin-users edge function, authenticated with the signed-in user's access token.
const callAdminUsers = async (action, payload = {}) => {
  const { data: sessionData } = await supabase.auth.getSession();
  const token = sessionData?.session?.access_token;
  const { data, error } = await supabase.functions.invoke('admin-users', {
    body: { action, ...payload },
    headers: { Authorization: `Bearer ${token}` }
  });
  if (error) {
    let message = error.message;
    try {
      const body = await error.context?.json();
      if (body?.error) message = body.error;
    } catch (_) {}
    throw new Error(message);
  }
  return data;
};

function Users({ userInfo, isAdmin, onLogout, onNavigate }) {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [sortBy, setSortBy] = useState('first_name');
  const [sortOrder, setSortOrder] = useState('asc');

  // Modals
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showCSVModal, setShowCSVModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);

  // Form states
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    stateLicenseNumber: '',
    pin: '',
    password: ''
  });

  // CSV upload
  const [csvFile, setCSVFile] = useState(null);
  const [csvErrors, setCSVErrors] = useState([]);

  // Notifications
  const [notification, setNotification] = useState(null);

  // Notification
  const showNotification = (message, type = 'info') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 4000);
  };

  // Fetch users
  const fetchUsers = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase.rpc('get_all_users');

      if (error) throw error;
      setUsers(data || []);
    } catch (error) {
      console.error('Error fetching users:', error);
      showNotification('Error loading users', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Filter and sort
  const applyFiltersAndSort = () => {
    let filtered = [...users];

    // Search filter
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(user =>
        user.first_name?.toLowerCase().includes(search) ||
        user.last_name?.toLowerCase().includes(search) ||
        user.email?.toLowerCase().includes(search) ||
        user.state_license_number?.toLowerCase().includes(search)
      );
    }

    // Role filter
    if (roleFilter !== 'all') {
      filtered = filtered.filter(user => {
        if (roleFilter === 'admin') return user.is_admin;
        if (roleFilter === 'editor') return user.is_editor && !user.is_admin;
        if (roleFilter === 'user') return !user.is_editor && !user.is_admin;
        return true;
      });
    }

    // Sort
    filtered.sort((a, b) => {
      let aVal = a[sortBy] || '';
      let bVal = b[sortBy] || '';

      if (sortBy === 'created_at') {
        aVal = new Date(aVal);
        bVal = new Date(bVal);
      }

      if (aVal < bVal) return sortOrder === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });

    setFilteredUsers(filtered);
  };

  // Toggle role
  const toggleRole = async (userId, role, currentValue) => {
    try {
      const updateData = {
        user_id: userId,
        first_name: null,
        last_name: null,
        state_license_number: null,
        user_pin: null,
        is_editor: role === 'editor' ? !currentValue : null,
        is_admin: role === 'admin' ? !currentValue : null
      };

      const { error } = await supabase.rpc('update_user_metadata', updateData);
      if (error) throw error;

      // Update local state
      setUsers(prev => prev.map(user =>
        user.id === userId
          ? { ...user, [role === 'editor' ? 'is_editor' : 'is_admin']: !currentValue }
          : user
      ));

      showNotification(`User role updated successfully`, 'success');
    } catch (error) {
      console.error('Error updating role:', error);
      showNotification('Failed to update user role', 'error');
    }
  };

  // Open edit modal
  const openEditModal = (user) => {
    setEditingUser(user);
    setFormData({
      firstName: user.first_name || '',
      lastName: user.last_name || '',
      email: user.email || '',
      stateLicenseNumber: user.state_license_number || '',
      pin: '',
      password: ''
    });
    setShowEditModal(true);
  };

  // Update user
  const handleUpdateUser = async () => {
    if (formData.pin && !PIN_PATTERN.test(formData.pin)) {
      showNotification('PIN must be exactly 6 digits', 'error');
      return;
    }
    try {
      // Update metadata
      const { error: metaError } = await supabase.rpc('update_user_metadata', {
        user_id: editingUser.id,
        first_name: formData.firstName,
        last_name: formData.lastName,
        state_license_number: formData.stateLicenseNumber,
        user_pin: formData.pin || null,
        is_editor: null,
        is_admin: null
      });

      if (metaError) throw metaError;

      // Update password if provided (requires service role)
      if (formData.password) {
        await callAdminUsers('reset_password', {
          user_id: editingUser.id,
          password: formData.password
        });
      }

      showNotification('User updated successfully', 'success');
      setShowEditModal(false);
      fetchUsers();
    } catch (error) {
      console.error('Error updating user:', error);
      showNotification('Failed to update user', 'error');
    }
  };

  // Delete user
  const handleDeleteUser = async () => {
    if (!window.confirm(`Are you sure you want to delete ${editingUser.email}? This cannot be undone.`)) {
      return;
    }

    try {
      await callAdminUsers('delete', { user_id: editingUser.id });

      showNotification('User deleted successfully', 'success');
      setShowEditModal(false);
      fetchUsers();
    } catch (error) {
      console.error('Error deleting user:', error);
      showNotification('Failed to delete user', 'error');
    }
  };

  // Add single user
  const handleAddUser = async () => {
    try {
      if (!formData.email || !formData.password) {
        showNotification('Email and password are required', 'error');
        return;
      }

      if (formData.pin && !PIN_PATTERN.test(formData.pin)) {
        showNotification('PIN must be exactly 6 digits', 'error');
        return;
      }

      // Create auth user via the admin-users edge function
      const authData = await callAdminUsers('create', {
        email: formData.email,
        password: formData.password
      });

      // Update metadata
      if (formData.firstName || formData.lastName || formData.stateLicenseNumber || formData.pin) {
        const { error: metaError } = await supabase.rpc('update_user_metadata', {
          user_id: authData.user.id,
          first_name: formData.firstName || null,
          last_name: formData.lastName || null,
          state_license_number: formData.stateLicenseNumber || null,
          user_pin: formData.pin || null,
          is_editor: null,
          is_admin: null
        });

        if (metaError) throw metaError;
      }

      showNotification('User created successfully', 'success');
      setShowAddModal(false);
      setFormData({ firstName: '', lastName: '', email: '', stateLicenseNumber: '', pin: '', password: '' });
      fetchUsers();
    } catch (error) {
      console.error('Error creating user:', error);
      showNotification(error.message || 'Failed to create user', 'error');
    }
  };

  // CSV Upload
  const handleCSVUpload = async () => {
    if (!csvFile) {
      showNotification('Please select a CSV file', 'error');
      return;
    }

    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const text = e.target.result;
        const rows = text.split('\n').filter(row => row.trim());
        const headers = rows[0].split(',').map(h => h.trim().toLowerCase());

        const errors = [];
        const usersToCreate = [];

        // Validate headers
        const requiredHeaders = ['email', 'password'];
        const missingHeaders = requiredHeaders.filter(h => !headers.includes(h));
        if (missingHeaders.length > 0) {
          showNotification(`Missing required columns: ${missingHeaders.join(', ')}`, 'error');
          return;
        }

        // Parse rows
        for (let i = 1; i < rows.length; i++) {
          const values = rows[i].split(',').map(v => v.trim());
          const user = {};

          headers.forEach((header, index) => {
            user[header] = values[index] || '';
          });

          if (!user.email || !user.password) {
            errors.push(`Row ${i + 1}: Missing email or password`);
            continue;
          }
          if (user.pin && !PIN_PATTERN.test(user.pin)) {
            errors.push(`Row ${i + 1}: PIN must be exactly 6 digits`);
            continue;
          }

          usersToCreate.push({
            email: user.email,
            password: user.password,
            firstName: user.firstname || user.first_name || '',
            lastName: user.lastname || user.last_name || '',
            license: user.license || user.state_license_number || '',
            pin: user.pin || ''
          });
        }

        if (errors.length > 0) {
          setCSVErrors(errors);
          return;
        }

        // Create users
        let created = 0;
        let failed = 0;

        // Create auth users via the admin-users edge function, in batches
        // Supabase will automatically reject duplicate emails
        const createResults = [];
        try {
          for (let i = 0; i < usersToCreate.length; i += 50) {
            const { results } = await callAdminUsers('bulk_create', {
              users: usersToCreate.slice(i, i + 50).map(u => ({ email: u.email, password: u.password }))
            });
            createResults.push(...results);
          }
        } catch (bulkError) {
          console.error('Bulk create failed:', bulkError);
          showNotification(bulkError.message || 'Failed to create users', 'error');
          return;
        }

        for (const [index, user] of usersToCreate.entries()) {
          try {
            console.log(`Creating user: ${user.email}`);

            const { id: newUserId, error: authError } = createResults[index];

            if (authError) {
              console.error(`Failed to create ${user.email}:`, authError);
              errors.push(`${user.email}: ${authError}`);
              failed++;
              continue;
            }

            console.log(`Successfully created auth user: ${user.email}`, newUserId);

            // Update metadata
            if (user.firstName || user.lastName || user.license || user.pin) {
              const { error: metaError } = await supabase.rpc('update_user_metadata', {
                user_id: newUserId,
                first_name: user.firstName || null,
                last_name: user.lastName || null,
                state_license_number: user.license || null,
                user_pin: user.pin || null,
                is_editor: null,
                is_admin: null
              });

              if (metaError) {
                console.warn(`Metadata update failed for ${user.email}:`, metaError);
                errors.push(`${user.email}: User created but metadata update failed - ${metaError.message}`);
              } else {
                console.log(`Metadata updated for ${user.email}`);
              }
            }

            created++;
            console.log(`✓ Successfully created: ${user.email}`);
          } catch (err) {
            console.error(`Exception creating ${user.email}:`, err);
            errors.push(`${user.email}: ${err.message}`);
            failed++;
          }
        }

        setCSVErrors(errors);

        if (created > 0) {
          showNotification(
            `✅ Successfully created ${created} user${created > 1 ? 's' : ''}!${failed > 0 ? ` (${failed} failed - see errors below)` : ''}`,
            'success'
          );
          fetchUsers();

          // Auto-close modal after 2 seconds if all succeeded
          if (failed === 0) {
            setTimeout(() => {
              setShowCSVModal(false);
              setCSVFile(null);
              setCSVErrors([]);
            }, 2000);
          }
        } else {
          showNotification(`❌ Failed to create any users. Check errors below.`, 'error');
        }
      } catch (error) {
        console.error('CSV parsing error:', error);
        showNotification('Failed to parse CSV file', 'error');
      }
    };

    reader.readAsText(csvFile);
  };

  // Download users as CSV
  const downloadUsersCSV = () => {
    const headers = ['Email', 'First Name', 'Last Name', 'License', 'PIN Reset Pending', 'Admin', 'Editor', 'Created'];
    const csvData = [
      headers.join(','),
      ...filteredUsers.map(user =>
        [
          user.email,
          user.first_name || '',
          user.last_name || '',
          user.state_license_number || '',
          user.pin_reset_required ? 'Yes' : 'No',
          user.is_admin ? 'Yes' : 'No',
          user.is_editor ? 'Yes' : 'No',
          new Date(user.created_at).toLocaleDateString()
        ].map(field => `"${field}"`).join(',')
      )
    ].join('\n');

    const blob = new Blob([csvData], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `users-${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  // UseEffects - must be before any conditional returns
  useEffect(() => {
    if (isAdmin) {
      fetchUsers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  useEffect(() => {
    applyFiltersAndSort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [users, searchTerm, roleFilter, sortBy, sortOrder]);

  // Access control - after all hooks
  if (!isAdmin) {
    return (
      <div className="container-center">
        <div className="card card-center">
          <h1 className="title">Access Denied</h1>
          <p className="subtitle">You do not have administrator privileges.</p>
          <button onClick={() => onNavigate('home')} className="btn btn-primary">
            Return to Dashboard
          </button>
        </div>
      </div>
    );
  }

  // Icons
  const SearchIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="11" cy="11" r="8"/>
      <path d="m21 21-4.35-4.35"/>
    </svg>
  );

  const FilterIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/>
    </svg>
  );

  const SortIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M11 5h10M11 9h7M11 13h4"/>
      <path d="m3 17 3 3 3-3M6 18V4"/>
    </svg>
  );

  const EditIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
    </svg>
  );

  const TrashIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
    </svg>
  );

  const PlusIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <line x1="12" y1="5" x2="12" y2="19"/>
      <line x1="5" y1="12" x2="19" y2="12"/>
    </svg>
  );

  const UploadIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
      <polyline points="17 8 12 3 7 8"/>
      <line x1="12" y1="3" x2="12" y2="15"/>
    </svg>
  );

  const InfoIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="10"/>
      <path d="M12 16v-4M12 8h.01"/>
    </svg>
  );

  const DownloadIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
      <polyline points="7 10 12 15 17 10"/>
      <line x1="12" y1="15" x2="12" y2="3"/>
    </svg>
  );

  return (
    <div style={{ minHeight: '100vh', background: '#f8f9fa' }}>
      <HamburgerMenu isAdmin={isAdmin} onLogout={onLogout} onNavigate={onNavigate} />

      {/* Notification Toast */}
      {notification && (
        <div style={{
          position: 'fixed',
          top: '80px',
          right: '20px',
          padding: '12px 20px',
          background: notification.type === 'error' ? '#d32f2f' : notification.type === 'success' ? '#2e7d32' : '#0E3293',
          color: 'white',
          borderRadius: '8px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          zIndex: 9999,
          fontSize: '14px',
          fontWeight: '500',
          maxWidth: '400px'
        }}>
          {notification.message}
        </div>
      )}

      <div style={{ padding: '80px 20px 20px', maxWidth: '1400px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ marginBottom: '30px' }}>
          <button
            onClick={() => onNavigate('admin-dashboard')}
            style={{
              padding: '8px 16px',
              border: '2px solid #0E3293',
              borderRadius: '6px',
              background: 'white',
              color: '#0E3293',
              fontSize: '13px',
              fontWeight: '600',
              cursor: 'pointer',
              marginBottom: '20px'
            }}
          >
            ← Back to Admin
          </button>

          <h1 style={{ fontSize: '24px', fontWeight: '700', color: '#333', marginBottom: '8px' }}>
            User Management
          </h1>
          <p style={{ color: '#666', fontSize: '14px' }}>
            {filteredUsers.length} of {users.length} users
          </p>
        </div>

        {/* Action Bar */}
        <div className="user-action-bar" style={{
          background: 'white',
          padding: '16px',
          borderRadius: '10px',
          marginBottom: '20px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          display: 'flex',
          gap: '10px',
          flexWrap: 'wrap',
          alignItems: 'center'
        }}>
          {/* Search */}
          <div style={{ flex: '1 1 200px', position: 'relative' }}>
            <div style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#999' }}>
              <SearchIcon />
            </div>
            <input
              type="text"
              placeholder="Search users..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                width: '100%',
                padding: '10px 10px 10px 40px',
                border: '1px solid #ddd',
                borderRadius: '6px',
                fontSize: '13px'
              }}
            />
          </div>

          {/* Role Filter */}
          <div style={{ position: 'relative' }}>
            <div style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#999', pointerEvents: 'none' }}>
              <FilterIcon />
            </div>
            <select
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
              style={{
                padding: '10px 12px 10px 40px',
                border: '1px solid #ddd',
                borderRadius: '6px',
                fontSize: '13px',
                background: 'white',
                cursor: 'pointer'
              }}
            >
              <option value="all">All Roles</option>
              <option value="admin">Admins</option>
              <option value="editor">Editors</option>
              <option value="user">Users</option>
            </select>
          </div>

          {/* Sort */}
          <div style={{ position: 'relative' }}>
            <div style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#999', pointerEvents: 'none' }}>
              <SortIcon />
            </div>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              style={{
                padding: '10px 12px 10px 40px',
                border: '1px solid #ddd',
                borderRadius: '6px',
                fontSize: '13px',
                background: 'white',
                cursor: 'pointer'
              }}
            >
              <option value="first_name">First Name</option>
              <option value="last_name">Last Name</option>
              <option value="email">Email</option>
              <option value="created_at">Date Created</option>
            </select>
          </div>

          {/* Sort Order */}
          <button
            onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
            style={{
              padding: '10px 16px',
              border: '1px solid #ddd',
              borderRadius: '6px',
              background: 'white',
              cursor: 'pointer',
              fontSize: '13px',
              fontWeight: '500'
            }}
          >
            {sortOrder === 'asc' ? '↑ A-Z' : '↓ Z-A'}
          </button>

          {/* Add User */}
          <button
            onClick={() => {
              setFormData({ firstName: '', lastName: '', email: '', stateLicenseNumber: '', pin: '', password: '' });
              setShowAddModal(true);
            }}
            style={{
              padding: '10px 16px',
              border: 'none',
              borderRadius: '6px',
              background: '#0E3293',
              color: 'white',
              cursor: 'pointer',
              fontSize: '13px',
              fontWeight: '600',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}
          >
            <PlusIcon /> Add User
          </button>

          {/* CSV Upload */}
          <button
            onClick={() => setShowCSVModal(true)}
            style={{
              padding: '10px 16px',
              border: '1px solid #0E3293',
              borderRadius: '6px',
              background: 'white',
              color: '#0E3293',
              cursor: 'pointer',
              fontSize: '13px',
              fontWeight: '600',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}
          >
            <UploadIcon /> CSV Upload
          </button>

          {/* Download CSV */}
          <button
            onClick={downloadUsersCSV}
            style={{
              padding: '10px 16px',
              border: '1px solid #ddd',
              borderRadius: '6px',
              background: 'white',
              cursor: 'pointer',
              fontSize: '13px',
              fontWeight: '500',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}
          >
            <DownloadIcon /> Export
          </button>
        </div>

        {/* Users List */}
        {loading ? (
          <div style={{ textAlign: 'center', padding: '60px' }}>
            <p style={{ color: '#666' }}>Loading users...</p>
          </div>
        ) : filteredUsers.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px' }}>
            <p style={{ color: '#666' }}>No users found</p>
          </div>
        ) : (
          <div style={{ display: 'grid', gap: '12px' }}>
            {filteredUsers.map(user => (
              <div
                key={user.id}
                className="user-card"
                style={{
                  background: 'white',
                  padding: '16px',
                  borderRadius: '10px',
                  boxShadow: '0 2px 6px rgba(0,0,0,0.06)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '16px',
                  flexWrap: 'wrap'
                }}
              >
                {/* User Info */}
                <div className="user-card-info" style={{ flex: '1 1 200px' }}>
                  <div style={{ fontWeight: '600', fontSize: '14px', color: '#333', marginBottom: '4px' }}>
                    {user.first_name || user.last_name ? `${user.first_name || ''} ${user.last_name || ''}`.trim() : 'No name set'}
                  </div>
                  <div style={{ fontSize: '12px', color: '#666' }}>{user.email}</div>
                  {user.pin_reset_required && (
                    <span style={{ display: 'inline-block', marginTop: '4px', padding: '2px 8px', borderRadius: '10px', background: '#fff3cd', color: '#856404', fontSize: '11px', fontWeight: '600' }}>
                      PIN reset pending
                    </span>
                  )}
                  {user.state_license_number && (
                    <div style={{ fontSize: '11px', color: '#999', marginTop: '2px' }}>
                      License: {user.state_license_number}
                    </div>
                  )}
                </div>

                {/* Toggles */}
                <div className="user-card-toggles" style={{ display: 'flex', gap: '20px', alignItems: 'center' }}>
                  {/* Editor Toggle */}
                  <div className="toggle-container" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontSize: '12px', color: '#666', fontWeight: '500' }}>Editor</span>
                    <div
                      onClick={() => toggleRole(user.id, 'editor', user.is_editor)}
                      style={{
                        width: '44px',
                        height: '24px',
                        borderRadius: '12px',
                        background: user.is_editor ? '#2e7d32' : '#ccc',
                        cursor: 'pointer',
                        position: 'relative',
                        transition: 'background 0.2s'
                      }}
                    >
                      <div style={{
                        width: '18px',
                        height: '18px',
                        borderRadius: '50%',
                        background: 'white',
                        position: 'absolute',
                        top: '3px',
                        left: user.is_editor ? '23px' : '3px',
                        transition: 'left 0.2s',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                      }} />
                    </div>
                  </div>

                  {/* Admin Toggle */}
                  <div className="toggle-container" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontSize: '12px', color: '#666', fontWeight: '500' }}>Admin</span>
                    <div
                      onClick={() => toggleRole(user.id, 'admin', user.is_admin)}
                      style={{
                        width: '44px',
                        height: '24px',
                        borderRadius: '12px',
                        background: user.is_admin ? '#0E3293' : '#ccc',
                        cursor: 'pointer',
                        position: 'relative',
                        transition: 'background 0.2s'
                      }}
                    >
                      <div style={{
                        width: '18px',
                        height: '18px',
                        borderRadius: '50%',
                        background: 'white',
                        position: 'absolute',
                        top: '3px',
                        left: user.is_admin ? '23px' : '3px',
                        transition: 'left 0.2s',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                      }} />
                    </div>
                  </div>
                </div>

                {/* Edit Button */}
                <div className="user-card-edit">
                  <button
                    onClick={() => openEditModal(user)}
                    style={{
                    padding: '8px 14px',
                    border: '1px solid #ddd',
                    borderRadius: '6px',
                    background: 'white',
                    cursor: 'pointer',
                    fontSize: '12px',
                    fontWeight: '500',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    color: '#333'
                  }}
                  >
                    <EditIcon /> Edit
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Edit User Modal */}
      {showEditModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
          padding: '20px'
        }}>
          <div className="user-modal" style={{
            background: 'white',
            borderRadius: '12px',
            padding: '24px',
            maxWidth: '500px',
            width: '100%',
            maxHeight: '90vh',
            overflowY: 'auto'
          }}>
            <h2 style={{ fontSize: '18px', fontWeight: '700', marginBottom: '20px', color: '#333' }}>
              Edit User
            </h2>

            <div style={{ display: 'grid', gap: '14px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', marginBottom: '6px', color: '#333' }}>
                  Email (read-only)
                </label>
                <input
                  type="email"
                  value={formData.email}
                  disabled
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #ddd',
                    borderRadius: '6px',
                    fontSize: '13px',
                    background: '#f5f5f5'
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', marginBottom: '6px', color: '#333' }}>
                  First Name
                </label>
                <input
                  type="text"
                  value={formData.firstName}
                  onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #ddd',
                    borderRadius: '6px',
                    fontSize: '13px'
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', marginBottom: '6px', color: '#333' }}>
                  Last Name
                </label>
                <input
                  type="text"
                  value={formData.lastName}
                  onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #ddd',
                    borderRadius: '6px',
                    fontSize: '13px'
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', marginBottom: '6px', color: '#333' }}>
                  State License Number
                </label>
                <input
                  type="text"
                  value={formData.stateLicenseNumber}
                  onChange={(e) => setFormData({ ...formData, stateLicenseNumber: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #ddd',
                    borderRadius: '6px',
                    fontSize: '13px'
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', marginBottom: '6px', color: '#333' }}>
                  New PIN (6 digits, optional)
                </label>
                <input
                  type="password"
                  inputMode="numeric"
                  maxLength={6}
                  autoComplete="new-password"
                  value={formData.pin}
                  onChange={(e) => setFormData({ ...formData, pin: e.target.value.replace(/\D/g, '') })}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #ddd',
                    borderRadius: '6px',
                    fontSize: '13px'
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', marginBottom: '6px', color: '#333' }}>
                  New Password (leave blank to keep current)
                </label>
                <input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  placeholder="Enter new password"
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #ddd',
                    borderRadius: '6px',
                    fontSize: '13px'
                  }}
                />
              </div>
            </div>

            <div style={{ display: 'flex', gap: '10px', marginTop: '24px' }}>
              <button
                onClick={handleUpdateUser}
                style={{
                  flex: 1,
                  padding: '12px',
                  border: 'none',
                  borderRadius: '6px',
                  background: '#0E3293',
                  color: 'white',
                  fontSize: '13px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                Save Changes
              </button>
              <button
                onClick={() => setShowEditModal(false)}
                style={{
                  flex: 1,
                  padding: '12px',
                  border: '1px solid #ddd',
                  borderRadius: '6px',
                  background: 'white',
                  color: '#666',
                  fontSize: '13px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
            </div>

            <button
              onClick={handleDeleteUser}
              style={{
                width: '100%',
                marginTop: '10px',
                padding: '12px',
                border: 'none',
                borderRadius: '6px',
                background: '#d32f2f',
                color: 'white',
                fontSize: '13px',
                fontWeight: '600',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px'
              }}
            >
              <TrashIcon /> Delete User
            </button>
          </div>
        </div>
      )}

      {/* Add User Modal */}
      {showAddModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
          padding: '20px'
        }}>
          <div className="user-modal" style={{
            background: 'white',
            borderRadius: '12px',
            padding: '24px',
            maxWidth: '500px',
            width: '100%',
            maxHeight: '90vh',
            overflowY: 'auto'
          }}>
            <h2 style={{ fontSize: '18px', fontWeight: '700', marginBottom: '20px', color: '#333' }}>
              Add New User
            </h2>

            <div style={{ display: 'grid', gap: '14px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', marginBottom: '6px', color: '#333' }}>
                  Email *
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="user@example.com"
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #ddd',
                    borderRadius: '6px',
                    fontSize: '13px'
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', marginBottom: '6px', color: '#333' }}>
                  Password *
                </label>
                <input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  placeholder="Minimum 6 characters"
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #ddd',
                    borderRadius: '6px',
                    fontSize: '13px'
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', marginBottom: '6px', color: '#333' }}>
                  First Name
                </label>
                <input
                  type="text"
                  value={formData.firstName}
                  onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #ddd',
                    borderRadius: '6px',
                    fontSize: '13px'
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', marginBottom: '6px', color: '#333' }}>
                  Last Name
                </label>
                <input
                  type="text"
                  value={formData.lastName}
                  onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #ddd',
                    borderRadius: '6px',
                    fontSize: '13px'
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', marginBottom: '6px', color: '#333' }}>
                  State License Number
                </label>
                <input
                  type="text"
                  value={formData.stateLicenseNumber}
                  onChange={(e) => setFormData({ ...formData, stateLicenseNumber: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #ddd',
                    borderRadius: '6px',
                    fontSize: '13px'
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', marginBottom: '6px', color: '#333' }}>
                  New PIN (6 digits, optional)
                </label>
                <input
                  type="password"
                  inputMode="numeric"
                  maxLength={6}
                  autoComplete="new-password"
                  value={formData.pin}
                  onChange={(e) => setFormData({ ...formData, pin: e.target.value.replace(/\D/g, '') })}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #ddd',
                    borderRadius: '6px',
                    fontSize: '13px'
                  }}
                />
              </div>
            </div>

            <div style={{ display: 'flex', gap: '10px', marginTop: '24px' }}>
              <button
                onClick={handleAddUser}
                style={{
                  flex: 1,
                  padding: '12px',
                  border: 'none',
                  borderRadius: '6px',
                  background: '#0E3293',
                  color: 'white',
                  fontSize: '13px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                Create User
              </button>
              <button
                onClick={() => setShowAddModal(false)}
                style={{
                  flex: 1,
                  padding: '12px',
                  border: '1px solid #ddd',
                  borderRadius: '6px',
                  background: 'white',
                  color: '#666',
                  fontSize: '13px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* CSV Upload Modal */}
      {showCSVModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
          padding: '20px'
        }}>
          <div className="user-modal" style={{
            background: 'white',
            borderRadius: '12px',
            padding: '24px',
            maxWidth: '600px',
            width: '100%',
            maxHeight: '90vh',
            overflowY: 'auto'
          }}>
            <h2 style={{ fontSize: '18px', fontWeight: '700', marginBottom: '20px', color: '#333' }}>
              CSV Bulk Upload
            </h2>

            {/* Info Box */}
            <div style={{
              background: '#e3f2fd',
              border: '1px solid #90caf9',
              borderRadius: '8px',
              padding: '14px',
              marginBottom: '20px',
              fontSize: '12px',
              color: '#0d47a1'
            }}>
              <div style={{ display: 'flex', alignItems: 'start', gap: '8px' }}>
                <div style={{ marginTop: '2px' }}><InfoIcon /></div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: '600', marginBottom: '8px' }}>CSV Format Requirements:</div>
                  <div style={{ marginBottom: '6px' }}>
                    <strong>Required:</strong> email, password
                  </div>
                  <div style={{ marginBottom: '6px' }}>
                    <strong>Optional:</strong> firstname, lastname, license, pin
                  </div>
                  <div style={{ marginBottom: '10px', fontSize: '11px', color: '#1565c0', fontStyle: 'italic' }}>
                    Note: Optional columns can be left empty. Users can complete their license/PIN in their profile later.
                  </div>
                  <div style={{ marginTop: '10px', background: 'white', borderRadius: '4px', overflow: 'auto' }}>
                    <table style={{ width: '100%', fontSize: '11px', fontFamily: 'monospace', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ background: '#f5f5f5' }}>
                          <th style={{ padding: '6px 8px', textAlign: 'left', borderBottom: '2px solid #ddd', fontWeight: '600' }}>email</th>
                          <th style={{ padding: '6px 8px', textAlign: 'left', borderBottom: '2px solid #ddd', fontWeight: '600' }}>password</th>
                          <th style={{ padding: '6px 8px', textAlign: 'left', borderBottom: '2px solid #ddd', fontWeight: '600' }}>firstname</th>
                          <th style={{ padding: '6px 8px', textAlign: 'left', borderBottom: '2px solid #ddd', fontWeight: '600' }}>lastname</th>
                          <th style={{ padding: '6px 8px', textAlign: 'left', borderBottom: '2px solid #ddd', fontWeight: '600' }}>license</th>
                          <th style={{ padding: '6px 8px', textAlign: 'left', borderBottom: '2px solid #ddd', fontWeight: '600' }}>pin</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td style={{ padding: '6px 8px', borderBottom: '1px solid #eee' }}>user@example.com</td>
                          <td style={{ padding: '6px 8px', borderBottom: '1px solid #eee' }}>TempPass123</td>
                          <td style={{ padding: '6px 8px', borderBottom: '1px solid #eee' }}>John</td>
                          <td style={{ padding: '6px 8px', borderBottom: '1px solid #eee' }}>Doe</td>
                          <td style={{ padding: '6px 8px', borderBottom: '1px solid #eee' }}>LIC123</td>
                          <td style={{ padding: '6px 8px', borderBottom: '1px solid #eee' }}>1234</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>

            {/* File Upload */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', marginBottom: '8px', color: '#333' }}>
                Select CSV File
              </label>
              <input
                type="file"
                accept=".csv"
                onChange={(e) => setCSVFile(e.target.files[0])}
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '1px solid #ddd',
                  borderRadius: '6px',
                  fontSize: '13px'
                }}
              />
            </div>

            {/* Errors */}
            {csvErrors.length > 0 && (
              <div style={{
                background: '#ffebee',
                border: '1px solid #ef9a9a',
                borderRadius: '8px',
                padding: '12px',
                marginBottom: '20px',
                maxHeight: '200px',
                overflowY: 'auto'
              }}>
                <div style={{ fontWeight: '600', fontSize: '12px', color: '#c62828', marginBottom: '8px' }}>
                  Errors:
                </div>
                {csvErrors.map((error, i) => (
                  <div key={i} style={{ fontSize: '11px', color: '#c62828', marginBottom: '4px' }}>
                    {error}
                  </div>
                ))}
              </div>
            )}

            <div style={{ display: 'flex', gap: '10px' }}>
              <button
                onClick={handleCSVUpload}
                disabled={!csvFile}
                style={{
                  flex: 1,
                  padding: '12px',
                  border: 'none',
                  borderRadius: '6px',
                  background: csvFile ? '#0E3293' : '#ccc',
                  color: 'white',
                  fontSize: '13px',
                  fontWeight: '600',
                  cursor: csvFile ? 'pointer' : 'not-allowed'
                }}
              >
                Upload and Process
              </button>
              <button
                onClick={() => {
                  setShowCSVModal(false);
                  setCSVFile(null);
                  setCSVErrors([]);
                }}
                style={{
                  flex: 1,
                  padding: '12px',
                  border: '1px solid #ddd',
                  borderRadius: '6px',
                  background: 'white',
                  color: '#666',
                  fontSize: '13px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Users;
